// pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';

const AdminDashboard = () => {
  const { apiCall, addNotification } = useAdmin();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalInheritances: 0,
    pendingEndowments: 0,
    changes: {
      totalUsers: 0,
      activeUsers: 0,
      totalInheritances: 0,
      pendingEndowments: 0
    }
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);
  

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load dashboard stats
      const statsResponse = await apiCall('https://phenixloc-api.fnvt.net/api/admin/dashboard/stats');
      setStats(statsResponse);

      // Load recent activity
      const activityResponse = await apiCall('https://phenixloc-api.fnvt.net/api/admin/dashboard/recent-activity');
      setRecentActivity(activityResponse.activities); 

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      addNotification('Error loading dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatActivityTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffDay > 0) {
      return diffDay === 1 ? 'Yesterday' : `${diffDay} days ago`;
    } else if (diffHour > 0) {
      return `${diffHour} ${diffHour === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffMin > 0) {
      return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`;
    } else {
      return 'Just now';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user': return 'fa-user-plus';
      case 'inheritance': return 'fa-file-alt';
      case 'delivery': return 'fa-hand-holding-heart';
      case 'admin': return 'fa-user-shield';
      default: return 'fa-sync-alt';
    }
  };

  if (loading) {
    return (
      <div className="admin-page-header">
        <h2>Dashboard</h2>
        <div className="admin-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-page-header">
        <h2>Dashboard</h2>
        <p>Welcome to PhenixLoc Admin Panel</p>
      </div>

      <div className="admin-stats-cards">
        <div className="admin-stat-card">
          <div className="admin-stat-card-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="admin-stat-card-info">
            <h4>Total Users</h4>
            <p className="admin-stat-value">{stats.totalUsers}</p>
            <p className={`admin-stat-change ${stats.changes.totalUsers >= 0 ? 'positive' : 'negative'}`}>
              {stats.changes.totalUsers >= 0 ? '+' : ''}{stats.changes.totalUsers}% 
              <span>this month</span>
            </p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card-icon">
            <i className="fas fa-hand-holding-heart"></i>
          </div>
          <div className="admin-stat-card-info">
            <h4>Total Inheritances</h4>
            <p className="admin-stat-value">{stats.totalInheritances}</p>
            <p className={`admin-stat-change ${stats.changes.totalInheritances >= 0 ? 'positive' : 'negative'}`}>
              {stats.changes.totalInheritances >= 0 ? '+' : ''}{stats.changes.totalInheritances}% 
              <span>this month</span>
            </p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card-icon">
            <i className="fas fa-user-check"></i>
          </div>
          <div className="admin-stat-card-info">
            <h4>Active Users</h4>
            <p className="admin-stat-value">{stats.activeUsers}</p>
            <p className={`admin-stat-change ${stats.changes.activeUsers >= 0 ? 'positive' : 'negative'}`}>
              {stats.changes.activeUsers >= 0 ? '+' : ''}{stats.changes.activeUsers}% 
              <span>this month</span>
            </p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="admin-stat-card-info">
            <h4>Pending Endowments</h4>
            <p className="admin-stat-value">{stats.pendingEndowments}</p>
            <p className={`admin-stat-change ${stats.changes.pendingEndowments >= 0 ? 'positive' : 'negative'}`}>
              {stats.changes.pendingEndowments >= 0 ? '+' : ''}{stats.changes.pendingEndowments}% 
              <span>this month</span>
            </p>
          </div>
        </div>
      </div>

      <div className="admin-dashboard-sections">
        <div className="admin-recent-activity">
          <div className="admin-section-header">
            <h3>Recent Activity</h3>
            <button className="admin-btn admin-btn-link">View All</button>
          </div>
          <div className="admin-activity-list">
            {recentActivity.length > 0 ? (
              recentActivity.map(activity => (
                <div key={activity.id} className="admin-activity-item">
                  <div className={`admin-activity-icon ${activity.type}-icon`}>
                    <i className={`fas ${getActivityIcon(activity.type)}`}></i>
                  </div>
                  <div className="admin-activity-details">
                    <p className="admin-activity-title">{activity.title}</p>
                    <p className="admin-activity-desc">{activity.description}</p>
                  </div>
                  <div className="admin-activity-time">
                    <p>{formatActivityTime(activity.timestamp)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-empty-state">
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;